import { Link, useNavigate, useParams } from 'react-router-dom'
import { parseAsStringLiteral, useQueryState } from 'nuqs'

import { useQuery } from '@apollo/client/react'
import { clsx } from 'clsx'

import {
  ArrowLeftIcon,
  ChannelAvatar,
  Flex,
  IconButton,
  Loader,
  PageHeader,
  PageLayout,
  Skeleton,
  Tabs,
} from '@buffer-mono/popcorn'

import { FeedbackWidget } from '~publish/components/FeedbackWidget'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { isService } from '~publish/helpers/channels'
import { getChannelNameForDisplay } from '~publish/helpers/getChannelNameForDisplay'
import { usePageTitle } from '~publish/hooks/usePageTitle'
import { GeneralSettings } from '~publish/legacy/general-settings'
import { isPublishSupported } from '~publish/utils/permissions/publishSupport'
import { isViewOnlyPublishChannel } from '~publish/utils/permissions/viewOnlyPublish'

import { PostingSchedule } from './PostingSchedule'
import { ChannelAccessDenied } from '../ChannelAccessDenied'
import { ChannelProvider } from '../ChannelContext'
import { GetChannelInfo } from '../ChannelPage'

import styles from './ChannelSettings.module.css'

import type { JSX } from 'react'

/**
 * This page integrates legacy components as a temporary solution. We should rewrite this page using GraphQL API and Popcorn
 */
export const ChannelSettings = ({
  isForNewSettings = false,
}: {
  // This is for when we embed this page in the new settings section of Publish
  // it is navigated to from clicking a specific channel there, so we show a
  // different header here (see below)
  isForNewSettings?: boolean
}): JSX.Element => {
  const navigate = useNavigate()
  const channelId =
    useParams<{
      id: string
    }>().id || ''
  const [tab, setTab] = useQueryState(
    'tab',
    parseAsStringLiteral(['general', 'posting-schedule'])
      .withDefault('posting-schedule')
      .withOptions({ history: 'push' }),
  )
  const tabTitle =
    tab === 'general'
      ? 'Channel Settings - General'
      : 'Channel Settings - Posting Schedule'
  usePageTitle(tabTitle)
  const organizationId = useOrganizationId()
  const { data, dataState, loading } = useQuery(GetChannelInfo, {
    variables: {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error - organizationId is not always defined we skip if it is not defined
      organizationId,
      channelId,
    },
    fetchPolicy: 'cache-and-network',
    skip: !organizationId,
  })
  const channel = dataState === 'complete' ? data.channel : undefined

  // A view-only member can still reach this route via a saved link; give it
  // the same access-denied treatment as the other single-channel routes.
  if (
    channel &&
    (!isPublishSupported(channel) || isViewOnlyPublishChannel(channel))
  ) {
    return <ChannelAccessDenied channel={channel} />
  }

  return (
    <Tabs
      asChild
      value={tab}
      onChange={(selectedTab): void => {
        if (selectedTab === 'general' || selectedTab === 'posting-schedule') {
          void setTab(selectedTab)
        }
      }}
    >
      <PageLayout className={clsx(loading && styles.loading)}>
        <PageHeader>
          {isForNewSettings ? (
            <PageHeader.Row>
              <IconButton
                as={Link}
                to={`/settings/channels`}
                label="Back to Channels"
                tooltip="Back to Channels"
                variant="tertiary"
              >
                <ArrowLeftIcon />
              </IconButton>
              <PageHeader.TitleGroup leadingGap="comfortable">
                <PageHeader.TitleLeading>
                  <ChannelAvatar
                    src={channel?.avatar}
                    channel={
                      isService(channel?.service)
                        ? channel.service
                        : 'instagram'
                    }
                    alt={`avatar for ${channel?.name}`}
                    size="mid"
                    className={styles.channelAvatar}
                  />
                </PageHeader.TitleLeading>
                <PageHeader.Title>
                  <span translate="no">
                    {channel ? getChannelNameForDisplay(channel) : null}
                  </span>
                </PageHeader.Title>
              </PageHeader.TitleGroup>
            </PageHeader.Row>
          ) : (
            <PageHeader.Row>
              <IconButton
                label="Back"
                tooltip="Back"
                variant="tertiary"
                onClick={(): void => navigate(-1)}
              >
                <ArrowLeftIcon />
              </IconButton>
              {channel ? (
                <PageHeader.TitleGroup leadingGap="comfortable">
                  <PageHeader.TitleLeading>
                    <ChannelAvatar
                      src={channel.avatar}
                      channel={
                        isService(channel.service)
                          ? channel.service
                          : 'instagram'
                      }
                      alt={`avatar for ${channel.name}`}
                      size="mid"
                      className={styles.channelAvatar}
                    />
                  </PageHeader.TitleLeading>
                  <PageHeader.Title>
                    <span translate="no">
                      {getChannelNameForDisplay(channel)}
                    </span>
                  </PageHeader.Title>
                  <PageHeader.TitleDescription>
                    Channel Settings
                  </PageHeader.TitleDescription>
                </PageHeader.TitleGroup>
              ) : (
                <Flex align="center" direction="row" gap="md">
                  <Skeleton rounded width={44} height={44} />
                  <Flex direction="column" gap="2xs">
                    <Skeleton rounded width={120} height={24} />
                    <Skeleton rounded width={100} height={16} />
                  </Flex>
                </Flex>
              )}

              <PageHeader.Actions>
                <FeedbackWidget id="channel-settings-page" source="publish" />
              </PageHeader.Actions>
            </PageHeader.Row>
          )}
          <PageHeader.Row>
            <Tabs.TabList>
              <Tabs.Tab value="posting-schedule">Posting Schedule</Tabs.Tab>
              <Tabs.Tab value="general">General</Tabs.Tab>
            </Tabs.TabList>
          </PageHeader.Row>
        </PageHeader>
        <PageLayout.Container className={styles.content}>
          {loading && (
            <Flex justify="center" align="center" fullWidth fullHeight>
              <Loader />
            </Flex>
          )}
          {!loading && channel && (
            <ChannelProvider channel={channel}>
              <Tabs.Panel value="posting-schedule">
                <PostingSchedule />
              </Tabs.Panel>
              <Tabs.Panel value="general">
                <GeneralSettings />
              </Tabs.Panel>
            </ChannelProvider>
          )}
        </PageLayout.Container>
      </PageLayout>
    </Tabs>
  )
}
